import { Fragment, lazy, Suspense, use, type ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ensurePageTranslations } from '@/i18n';
import AppFrame from '@/components/organisms/AppFrame';
import AppNavigation from '@/components/organisms/AppNavigation';
import MvpConnection from '@/components/organisms/MvpConnection';
import { firstMvp } from '@/lib/productScope';
import BrandLoading from '@/components/organisms/BrandLoading';
import { SearchConversationDraftProvider } from '@/components/providers/SearchConversationDraft';
import { useTokenKeepAlive } from '@/hooks/useTokenKeepAlive';
import { PageEntryLoadProvider } from '@/lib/pageEntryLoad';

const SystemGuard = lazy(() => import('@/components/organisms/SystemGuard'));
const pageTranslationsReady = new Map<string, Promise<void>>();

/** A stable boundary preserves the layout; its cached promise contains only public labels. */
function PageTranslations({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  if (!firstMvp || pathname !== '/') {
    let ready = pageTranslationsReady.get(locale);
    if (!ready) {
      ready = ensurePageTranslations(locale);
      pageTranslationsReady.set(locale, ready);
    }
    use(ready);
  }
  return <>{children}</>;
}
const ConnectionGuard = firstMvp ? MvpConnection : SystemGuard;
const DraftBoundary =
  firstMvp &&
  !(import.meta.env.DEV && import.meta.env.VITE_ENABLE_MSW === 'true')
    ? SearchConversationDraftProvider
    : Fragment;

/**
 * ≥1024px: 전체 화면 배경 위에 중앙 정렬한 브랜드/앱(최대1120px·간격32px), 앱 최대480px.
 * <1024px: 기존 중앙 App Frame(최대480px)을 유지한다 (실행계획 6.2).
 */
export default function RootLayout() {
  useTokenKeepAlive();
  return (
    <AppFrame>
      <DraftBoundary>
        <Suspense fallback={<BrandLoading />}>
          <ConnectionGuard>
            <Suspense fallback={<BrandLoading />}>
              <PageTranslations>
                <PageEntryLoadProvider>
                  <Outlet />
                </PageEntryLoadProvider>
              </PageTranslations>
            </Suspense>
          </ConnectionGuard>
        </Suspense>
      </DraftBoundary>
      <AppNavigation />
    </AppFrame>
  );
}
